import { Identifier } from '@okletsplay/core';
import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { MatchEntity } from 'app/interfaces/entity';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { cancelationSubscribe } from 'app/services/api/utils';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import {
  AcceptJoinRequestRequest,
  DeclineJoinRequestRequest,
} from 'modules/matches/store/current-match/interfaces';
import { CurrentMatchState } from './interfaces';
import { CurrentMatchStoreStatus } from './enums';

export const getMatchById = createAsyncThunk(
  `${EntityStoreName.Matches}/getMatchById`,
  (id: Identifier, { signal }): Promise<MatchEntity> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<MatchEntity>({
      params: {
        method: 'GET',
        resource: ApiResource.Matches,
        urlPrefix: id,
        cancelToken,
      },
    });
  }
);

export const getMatchGameUrl = createAsyncThunk(
  `${EntityStoreName.Matches}/getMatchGameUrl`,
  (id: Identifier, { signal }): Promise<string> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<string>({
      params: {
        method: 'GET',
        resource: ApiResource.Matches,
        urlPrefix: `${id}/game-url`,
        cancelToken,
      },
    });
  }
);

export const joinMatchRequest = createAsyncThunk(
  `${EntityStoreName.Matches}/joinMatchRequest`,
  (id: Identifier): Promise<MatchEntity> => {
    return apiRequestCall<MatchEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Matches,
        urlPrefix: `${id}/join`,
      },
    });
  }
);

export const forfeitMatchRequest = createAsyncThunk(
  `${EntityStoreName.Matches}/forfeitMatchRequest`,
  (id: Identifier): Promise<MatchEntity> => {
    return apiRequestCall<MatchEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Matches,
        urlPrefix: `${id}/forfeit`,
      },
      successRequestMessage: 'Forfeit request has been sent.',
    });
  }
);

export const acceptInvite = createAsyncThunk(
  `${EntityStoreName.Matches}/acceptInvite`,
  (id: Identifier): Promise<MatchEntity> => {
    return apiRequestCall<MatchEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Matches,
        urlPrefix: `${id}/accept-invite`,
      },
      successRequestMessage: 'Accept invite request has been sent.',
    });
  }
);

export const declineInvite = createAsyncThunk(
  `${EntityStoreName.Matches}/declineInvite`,
  (id: Identifier): Promise<MatchEntity> => {
    return apiRequestCall<MatchEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Matches,
        urlPrefix: `${id}/decline-invite`,
      },
      successRequestMessage: 'Decline invite request has been sent.',
    });
  }
);

export const acceptJoin = createAsyncThunk(
  `${EntityStoreName.Matches}/acceptJoin`,
  ({ matchId, userId }: AcceptJoinRequestRequest): Promise<MatchEntity> => {
    return apiRequestCall<MatchEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Matches,
        urlPrefix: `${matchId}/join/${userId}/accept`,
      },
      successRequestMessage: 'Accept join request has been sent.',
    });
  }
);

export const declineJoin = createAsyncThunk(
  `${EntityStoreName.Matches}/declineJoin`,
  ({ matchId, userId }: DeclineJoinRequestRequest): Promise<MatchEntity> => {
    return apiRequestCall<MatchEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Matches,
        urlPrefix: `${matchId}/join/${userId}/decline`,
      },
      successRequestMessage: 'Decline join request has been sent.',
    });
  }
);

export const withdraw = createAsyncThunk(
  `${EntityStoreName.Matches}/withdraw`,
  (id: Identifier): Promise<MatchEntity> => {
    return apiRequestCall<MatchEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Matches,
        urlPrefix: `${id}/withdraw`,
      },
      successRequestMessage: 'Withdraw request has been sent.',
    });
  }
);

export const updateMatch = createAsyncThunk(
  `${EntityStoreName.Matches}/updateMatch`,
  (id: Identifier): Promise<MatchEntity> => {
    return apiRequestCall<MatchEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Matches,
        urlPrefix: `${id}/win-animation`,
      },
    });
  }
);

export const registerReducers = (builder: ActionReducerMapBuilder<CurrentMatchState>): void => {
  builder
    .addCase(getMatchById.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, CurrentMatchStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getMatchById.fulfilled, (state, action) => {
      state.entity = action.payload;
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getMatchById.rejected, (state, action) => {
      state.entity = {};
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getMatchGameUrl.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, CurrentMatchStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getMatchGameUrl.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getMatchGameUrl.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(joinMatchRequest.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, CurrentMatchStoreStatus.Joining, action.meta.requestId);
    })
    .addCase(joinMatchRequest.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Joining,
        action.meta.requestId
      );
    })
    .addCase(joinMatchRequest.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Joining,
        action.meta.requestId
      );
    })
    .addCase(acceptInvite.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentMatchStoreStatus.Accepting,
        action.meta.requestId
      );
    })
    .addCase(acceptInvite.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Accepting,
        action.meta.requestId
      );
    })
    .addCase(acceptInvite.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Accepting,
        action.meta.requestId
      );
    })
    .addCase(declineInvite.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentMatchStoreStatus.Declining,
        action.meta.requestId
      );
    })
    .addCase(declineInvite.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Declining,
        action.meta.requestId
      );
    })
    .addCase(declineInvite.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Declining,
        action.meta.requestId
      );
    })
    .addCase(withdraw.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentMatchStoreStatus.Withdrawing,
        action.meta.requestId
      );
    })
    .addCase(withdraw.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Withdrawing,
        action.meta.requestId
      );
    })
    .addCase(withdraw.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Withdrawing,
        action.meta.requestId
      );
    })
    .addCase(acceptJoin.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, CurrentMatchStoreStatus.Joining, action.meta.requestId);
    })
    .addCase(acceptJoin.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Joining,
        action.meta.requestId
      );
    })
    .addCase(acceptJoin.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Joining,
        action.meta.requestId
      );
    })
    .addCase(declineJoin.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentMatchStoreStatus.Declining,
        action.meta.requestId
      );
    })
    .addCase(declineJoin.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Declining,
        action.meta.requestId
      );
    })
    .addCase(declineJoin.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Declining,
        action.meta.requestId
      );
    })
    .addCase(updateMatch.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentMatchStoreStatus.ViewingAnimation,
        action.meta.requestId
      );
    })
    .addCase(updateMatch.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.ViewingAnimation,
        action.meta.requestId
      );
    })
    .addCase(updateMatch.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.ViewingAnimation,
        action.meta.requestId
      );
    })
    .addCase(forfeitMatchRequest.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentMatchStoreStatus.Forfeiting,
        action.meta.requestId
      );
    })
    .addCase(forfeitMatchRequest.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Forfeiting,
        action.meta.requestId
      );
    })
    .addCase(forfeitMatchRequest.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentMatchStoreStatus.Forfeiting,
        action.meta.requestId
      );
    });
};
